import type { MouseEvent as ReactMouseEvent } from "react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "../../../components/ui/Modal";
import type { AppliedFilterItem, PriceDetailsItem } from "../types";
import { formatPrice } from "../utils";

type Props = {
  open: boolean;
  title: string;
  subtitle?: string;
  appliedFilters?: AppliedFilterItem[];
  data: PriceDetailsItem[];
  loading: boolean;
  error?: string | null;
  onClose: () => void;
};

type FlatRow = {
  id: string;
  hotelId: string;
  hotelName: string;
  roomKey: string;
  roomName: string;
  category: string;
  platformKey: string;
  platformLabel: string;
  price: number;
  isBase: boolean;
};

type DisplayRow = FlatRow & {
  showHotel: boolean;
  hotelRowSpan: number;
  showRoom: boolean;
  roomRowSpan: number;
};

type OrderMaps = {
  categoryOrder: Map<string, number>;
  hotelOrder: Map<string, number>;
};

const PLATFORM_LABELS: Record<string, string> = {
  yandex: "Яндекс",
  tinkoff: "Т-Путешествия",
  tbank: "Т-Путешествия",
  ostrovok: "Островок",
  sutochno: "Суточно",
  traveline: "Сайт отеля",
  travelline: "Сайт отеля",
  alean: "Alean",
  booking: "Booking",
};

const PLATFORM_ORDER: Record<string, number> = {
  yandex: 1,
  tinkoff: 2,
  tbank: 2,
  ostrovok: 3,
  sutochno: 4,
  traveline: 5,
  travelline: 5,
  alean: 6,
  booking: 7,
};

const HIDDEN_PLATFORMS = new Set(["sutochno"]);
const MIN_COLUMN_WIDTH = 120;
const DEFAULT_COLUMN_WIDTHS = {
  hotel: 340,
  room: 230,
  platform: 200,
  price: 170,
};

const normalizePlatform = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "tbank") return "tinkoff";
  if (normalized === "travelline") return "traveline";
  return normalized;
};

const isMissingPrice = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === 0 || value === "0" || value === "none";

const formatPlatformLabel = (value: string) =>
  PLATFORM_LABELS[value] ??
  value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatCategoryLabel = (value: string) =>
  value
    ? value
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : "Без категории";

const getPlatformOrder = (value: string) => PLATFORM_ORDER[value] ?? Number.MAX_SAFE_INTEGER;

const getMedian = (values: number[]) => {
  if (!values.length) return Number.POSITIVE_INFINITY;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
};

const getAverage = (values: number[]) => {
  if (!values.length) return Number.POSITIVE_INFINITY;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const buildOrderMaps = (rows: FlatRow[]): OrderMaps => {
  const categoryValues = new Map<string, number[]>();
  const roomMedianMap = new Map<string, number>();

  rows.forEach((row) => {
    const categoryBucket = categoryValues.get(row.category) ?? [];
    categoryBucket.push(row.price);
    categoryValues.set(row.category, categoryBucket);
  });

  const roomPriceMap = new Map<string, number[]>();
  rows.forEach((row) => {
    const roomBucket = roomPriceMap.get(row.roomKey) ?? [];
    roomBucket.push(row.price);
    roomPriceMap.set(row.roomKey, roomBucket);
  });

  roomPriceMap.forEach((prices, roomKey) => {
    roomMedianMap.set(roomKey, getMedian(prices));
  });

  const categoryOrder = new Map(
    Array.from(categoryValues.entries())
      .map(([category, prices]) => ({
        category,
        median: getMedian(prices),
      }))
      .sort((left, right) => {
        if (left.median !== right.median) return left.median - right.median;
        return formatCategoryLabel(left.category).localeCompare(formatCategoryLabel(right.category), "ru");
      })
      .map((item, index) => [item.category, index] as const),
  );

  const hotelRoomMedianMap = new Map<string, number[]>();
  const seenHotelRoomKeys = new Set<string>();
  rows.forEach((row) => {
    const compositeRoomKey = `${row.category}::${row.hotelId}::${row.roomKey}`;
    if (seenHotelRoomKeys.has(compositeRoomKey)) return;
    seenHotelRoomKeys.add(compositeRoomKey);
    const roomMedian = roomMedianMap.get(row.roomKey);
    if (roomMedian === undefined) return;
    const hotelBucketKey = `${row.category}::${row.hotelId}`;
    const hotelBucket = hotelRoomMedianMap.get(hotelBucketKey) ?? [];
    hotelBucket.push(roomMedian);
    hotelRoomMedianMap.set(hotelBucketKey, hotelBucket);
  });

  const hotelOrder = new Map(
    Array.from(hotelRoomMedianMap.entries())
      .map(([key, medians]) => ({
        key,
        averageMedian: getAverage(medians),
        hotelName: rows.find((row) => `${row.category}::${row.hotelId}` === key)?.hotelName ?? "",
      }))
      .sort((left, right) => {
        if (left.averageMedian !== right.averageMedian) return left.averageMedian - right.averageMedian;
        return left.hotelName.localeCompare(right.hotelName, "ru");
      })
      .map((item, index) => [item.key, index] as const),
  );

  return { categoryOrder, hotelOrder };
};

const sortRows = (rows: FlatRow[], orderMaps: OrderMaps) =>
  [...rows].sort((left, right) => {
    const leftCategoryOrder = orderMaps.categoryOrder.get(left.category) ?? Number.MAX_SAFE_INTEGER;
    const rightCategoryOrder = orderMaps.categoryOrder.get(right.category) ?? Number.MAX_SAFE_INTEGER;
    if (leftCategoryOrder !== rightCategoryOrder) return leftCategoryOrder - rightCategoryOrder;

    const leftHotelOrder = orderMaps.hotelOrder.get(`${left.category}::${left.hotelId}`) ?? Number.MAX_SAFE_INTEGER;
    const rightHotelOrder = orderMaps.hotelOrder.get(`${right.category}::${right.hotelId}`) ?? Number.MAX_SAFE_INTEGER;
    if (leftHotelOrder !== rightHotelOrder) return leftHotelOrder - rightHotelOrder;

    if (left.price !== right.price) return left.price - right.price;

    const roomCompare = left.roomName.localeCompare(right.roomName, "ru");
    if (roomCompare !== 0) return roomCompare;

    const platformCompare = getPlatformOrder(left.platformKey) - getPlatformOrder(right.platformKey);
    if (platformCompare !== 0) return platformCompare;

    return left.platformLabel.localeCompare(right.platformLabel, "ru");
  });

const buildDisplayRows = (sourceRows: FlatRow[]): DisplayRow[] => {
  const rows = sourceRows.map((row) => ({
    ...row,
    showHotel: false,
    hotelRowSpan: 0,
    showRoom: false,
    roomRowSpan: 0,
  }));

  let start = 0;
  while (start < rows.length) {
    let end = start + 1;
    while (end < rows.length && rows[end].hotelName === rows[start].hotelName) {
      end += 1;
    }

    rows[start].showHotel = true;
    rows[start].hotelRowSpan = end - start;

    let roomStart = start;
    while (roomStart < end) {
      let roomEnd = roomStart + 1;
      while (roomEnd < end && rows[roomEnd].roomKey === rows[roomStart].roomKey) {
        roomEnd += 1;
      }
      rows[roomStart].showRoom = true;
      rows[roomStart].roomRowSpan = roomEnd - roomStart;
      roomStart = roomEnd;
    }

    start = end;
  }

  return rows;
};

export default function PriceDetailsModal({
  open,
  title,
  subtitle,
  appliedFilters = [],
  data,
  loading,
  error,
  onClose,
}: Props) {
  const [search, setSearch] = useState("");
  const [showOnlyMinPrices, setShowOnlyMinPrices] = useState(false);
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);
  const resizeRef = useRef<{ key: keyof typeof DEFAULT_COLUMN_WIDTHS; startX: number; startWidth: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch("");
    setShowOnlyMinPrices(false);
  }, [open]);

  const baseRows = useMemo<FlatRow[]>(() => {
    return data.flatMap((item) => {
      const normalizedPlatforms = new Map<string, number>();
      Object.entries(item.prices ?? {}).forEach(([platform, value]) => {
        if (isMissingPrice(value)) return;
        const parsed = Number(value);
        if (Number.isNaN(parsed)) return;
        const normalizedPlatform = normalizePlatform(platform);
        if (HIDDEN_PLATFORMS.has(normalizedPlatform)) return;
        const current = normalizedPlatforms.get(normalizedPlatform);
        if (current === undefined || parsed < current) {
          normalizedPlatforms.set(normalizedPlatform, parsed);
        }
      });

      return Array.from(normalizedPlatforms.entries())
        .sort(([left], [right]) => {
          const leftOrder = getPlatformOrder(left);
          const rightOrder = getPlatformOrder(right);
          if (leftOrder !== rightOrder) return leftOrder - rightOrder;
          return left.localeCompare(right, "ru");
        })
        .map(([platformKey, price]) => ({
          id: `${item.hotelId}-${item.roomKey}-${platformKey}`,
          hotelId: item.hotelId,
          hotelName: item.hotelName,
          roomKey: item.roomKey,
          roomName: item.roomName,
          category: item.category,
          platformKey,
          platformLabel: formatPlatformLabel(platformKey),
          price,
          isBase: Boolean(item.isBase),
        }));
    });
  }, [data]);

  const orderMaps = useMemo(() => buildOrderMaps(baseRows), [baseRows]);

  const expandedRows = useMemo<FlatRow[]>(() => {
    const sortedRows = sortRows(baseRows, orderMaps);
    if (!showOnlyMinPrices) return sortedRows;

    const minRowsByCategoryHotel = new Map<string, FlatRow>();
    sortedRows.forEach((row) => {
      const key = `${row.category}::${row.hotelId}`;
      const current = minRowsByCategoryHotel.get(key);
      if (!current || row.price < current.price) {
        minRowsByCategoryHotel.set(key, row);
      }
    });

    return sortRows(Array.from(minRowsByCategoryHotel.values()), orderMaps);
  }, [baseRows, orderMaps, showOnlyMinPrices]);

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return expandedRows;
    return expandedRows.filter((row) =>
      [row.hotelName, row.roomName, row.platformLabel, formatCategoryLabel(row.category)].some((value) =>
        value.toLowerCase().includes(query),
      ),
    );
  }, [expandedRows, search]);

  const categoryGroups = useMemo(() => {
    const grouped = new Map<string, FlatRow[]>();
    filteredRows.forEach((row) => {
      const bucket = grouped.get(row.category) ?? [];
      bucket.push(row);
      grouped.set(row.category, bucket);
    });

    return Array.from(grouped.entries()).map(([category, rows]) => ({
      category,
      categoryLabel: formatCategoryLabel(category),
      rows: buildDisplayRows(rows),
    }));
  }, [filteredRows]);

  const minMax = useMemo(() => {
    const values = filteredRows.map((row) => row.price);
    if (!values.length) return { min: 0, max: 0 };
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [filteredRows]);

  const getPriceCellStyle = (value: number) => {
    if (minMax.max <= minMax.min) {
      return { backgroundColor: "rgba(148, 163, 184, 0.18)" };
    }
    const ratio = (value - minMax.min) / (minMax.max - minMax.min);
    const hue = 95 - ratio * 75;
    return { backgroundColor: `hsla(${hue.toFixed(0)}, 56%, 70%, 0.55)` };
  };

  const shownHotels = useMemo(() => new Set(filteredRows.map((row) => row.hotelName)).size, [filteredRows]);

  const handleResizeMove = useCallback((event: MouseEvent) => {
    const state = resizeRef.current;
    if (!state) return;
    const nextWidth = Math.max(MIN_COLUMN_WIDTH, state.startWidth + event.clientX - state.startX);
    setColumnWidths((current) => ({
      ...current,
      [state.key]: nextWidth,
    }));
  }, []);

  const stopResize = useCallback(() => {
    resizeRef.current = null;
    window.removeEventListener("mousemove", handleResizeMove);
    window.removeEventListener("mouseup", stopResize);
  }, [handleResizeMove]);

  const startResize = (key: keyof typeof DEFAULT_COLUMN_WIDTHS, event: ReactMouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = {
      key,
      startX: event.clientX,
      startWidth: columnWidths[key],
    };
    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", stopResize);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", stopResize);
    };
  }, [handleResizeMove, stopResize]);

  const hasData = expandedRows.length > 0;

  return (
    <Modal open={open} onClose={onClose} title={title} widthClass="max-w-[76vw]">
      <div className="flex flex-col gap-4">
        {subtitle && <p className="-mt-1 text-sm text-slate-500">{subtitle}</p>}

        {appliedFilters.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {appliedFilters.map((filter) => (
              <span
                key={`${filter.label}-${filter.value}`}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-600"
              >
                <span className="font-semibold text-slate-700">{filter.label}:</span>
                <span>{filter.value}</span>
              </span>
            ))}
          </div>
        )}

        {loading && (
          <div className="grid min-h-[320px] place-items-center rounded-3xl border border-slate-200 bg-slate-50/70">
            <div className="flex items-center gap-4 text-base font-semibold text-slate-600">
              <span className="h-10 w-10 animate-spin rounded-full border-[3px] border-indigo-200 border-t-indigo-600" />
              Загружаем детализацию...
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {error}
          </div>
        )}

        {!loading && !error && hasData && (
          <>
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Поиск: категория, отель, номер, платформа"
                  className="h-11 min-w-[260px] rounded-2xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 outline-none transition focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100"
                />
                <button
                  type="button"
                  onClick={() => setShowOnlyMinPrices((current) => !current)}
                  aria-pressed={showOnlyMinPrices}
                  className={[
                    "inline-flex h-11 items-center justify-between gap-3 rounded-2xl border px-4 text-sm font-semibold transition",
                    showOnlyMinPrices
                      ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                  ].join(" ")}
                >
                  <span>Только мин. цены</span>
                  <span
                    className={[
                      "relative inline-flex h-6 w-11 items-center rounded-full transition",
                      showOnlyMinPrices ? "bg-indigo-600" : "bg-slate-300",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "inline-block h-5 w-5 rounded-full bg-white shadow-sm transition",
                        showOnlyMinPrices ? "translate-x-5" : "translate-x-0.5",
                      ].join(" ")}
                    />
                  </span>
                </button>
              </div>
              <div className="text-sm text-slate-500">
                Отелей: {shownHotels} | Строк: {filteredRows.length}
              </div>
            </div>

            <div className="max-h-[70vh] overflow-auto rounded-3xl border border-slate-200">
              <table className="min-w-full border-collapse text-sm">
                <colgroup>
                  <col style={{ width: columnWidths.hotel }} />
                  <col style={{ width: columnWidths.room }} />
                  <col style={{ width: columnWidths.platform }} />
                  <col style={{ width: columnWidths.price }} />
                </colgroup>
                <thead className="sticky top-0 z-20">
                  <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                    <th className="relative border-r border-slate-200 px-4 py-3 text-left">
                      Отель
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("hotel", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                    <th className="relative border-r border-slate-200 px-4 py-3 text-left">
                      Номер
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("room", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                    <th className="relative border-r border-slate-200 px-4 py-3 text-left">
                      Платформа
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("platform", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                    <th className="relative px-4 py-3 text-right">
                      Цена
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        onMouseDown={(event) => startResize("price", event)}
                        className="absolute right-0 top-0 h-full w-3 cursor-col-resize touch-none"
                      >
                        <span className="absolute right-1 top-1/2 h-8 w-px -translate-y-1/2 bg-slate-300" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categoryGroups.length > 0 ? (
                    categoryGroups.map((group) => (
                      <Fragment key={`group-${group.category}`}>
                        <tr className="border-y border-slate-200 bg-slate-100/80">
                          <td colSpan={4} className="px-4 py-2">
                            <span className="inline-flex rounded-xl border border-slate-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                              Категория: {group.categoryLabel}
                            </span>
                          </td>
                        </tr>
                        {group.rows.map((row) => (
                          <tr
                            key={`${group.category}-${row.id}`}
                            className={[
                              "border-b border-slate-100 last:border-b-0",
                              row.showRoom ? "border-t-2 border-t-slate-300" : "",
                              row.isBase ? "bg-amber-50/60" : "bg-white",
                            ].join(" ")}
                          >
                            {row.showHotel && (
                              <td
                                rowSpan={row.hotelRowSpan}
                                className={[
                                  "border-r border-slate-100 px-4 py-3 align-top font-semibold",
                                  row.isBase ? "text-amber-900" : "text-slate-700",
                                ].join(" ")}
                              >
                                {row.hotelName}
                              </td>
                            )}

                            {row.showRoom && (
                              <td
                                rowSpan={row.roomRowSpan}
                                className="border-r border-slate-100 bg-slate-50/60 px-4 py-3 align-top font-medium text-slate-700"
                              >
                                {row.roomName}
                              </td>
                            )}

                            <td className="border-r border-slate-100 px-4 py-3 text-slate-600">{row.platformLabel}</td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-800" style={getPriceCellStyle(row.price)}>
                              {formatPrice(row.price)}
                            </td>
                          </tr>
                        ))}
                      </Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-slate-500">
                        По вашему запросу ничего не найдено
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!loading && !error && !hasData && (
          <div className="grid min-h-[220px] place-items-center rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-500">
            Нет данных для отображения
          </div>
        )}
      </div>
    </Modal>
  );
}
